import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VerifiedIcon from "@mui/icons-material/Verified";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ReviewSection from "./ReviewSection"; // now self-fetches from Sanity

// Theme-styled components for the Hero section
const AboutHero = styled(Box)(({ theme }) => ({
  backgroundColor: "#DCD3C7", // Solid background color
  color: "#333333", // Text color
  padding: theme.spacing(6, 2),
  textAlign: "center",
}));

// Theme-styled components for the Value Cards
const ValueCard = styled(Card)(({ theme }) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  textAlign: "center",
  borderRadius: 16,
  boxShadow: theme.shadows[3],
  transition: "none", // Disable all transitions
  margin: 0,
  "& .MuiCardContent-root": {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    textAlign: "center",
  },
  "&:hover": {
    transform: "none !important",
    boxShadow: `${theme.shadows[3]} !important`,
  },
}));

// Icon background circle
const IconWrap = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  margin: "0 auto",
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  color: theme.palette.primary.main,
  backgroundColor: "rgba(107, 68, 35, 0.10)",
}));

const AboutPage = () => {
  const location = useLocation();

  // Scroll to top when component mounts or location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const values = [
    {
      icon: <VerifiedIcon sx={{ fontSize: 34 }} />,
      title: "Quality Assurance",
      description:
        "We guarantee the highest quality materials and workmanship in every project.",
    },
    {
      icon: <HandshakeIcon sx={{ fontSize: 34 }} />,
      title: "Trust & Reliability",
      description:
        "Building lasting relationships through transparent communication and reliable service.",
    },
    {
      icon: <EmojiEventsIcon sx={{ fontSize: 34 }} />,
      title: "Excellence",
      description:
        "Committed to exceeding expectations and delivering exceptional results.",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 34 }} />,
      title: "Continuous Improvement",
      description:
        "Each project is an opportunity to learn, refine, and elevate the standard of our craft.",
    },
  ];

  return (
    <>
      <AboutHero>
        <Container>
          <Typography
            sx={{ color: "#333333", textAlign: "left" }}
            variant="h2"
            component="h1"
            gutterBottom
            fontWeight="regular"
          >
            Our Story
          </Typography>
          <Typography variant="h6" component="p" sx={{ color: "#333333", textAlign: "left" }}>
            Our journey began with a simple idea to turn a new house into a home through thoughtful, hands-on design and woodworking. What started as a personal passion project has grown into a trusted home improvement service offering a range of solutions from garage epoxy flooring, accent walls and wall paneling, building a fireplace and installation, custom closets, outdoor lighting, and much more.
            <br />
            We are committed to delivering high-quality craftsmanship, ensuring that every customer receives exceptional value without compromising on quality. Precision, durability, and attention to detail are at the heart of everything we do.
            <br />
            With each project, our expertise has grown, allowing us to continually refine our work and bring new ideas to life. Every build is an opportunity to apply past learnings and create something that blends function, beauty, and lasting quality.
            Our goal is simple: to help transform your spaces into warm, inviting, and functional extensions of your lifestyle. 
            <br />
            <b>You think, we make it happen!</b>

          </Typography>
        </Container>
      </AboutHero>

      <Container sx={{ py: 8 }}>
        {/* Our Values */}
        <Box sx={{ mt: 4, mb: 10 }}>
          <Typography variant="h2" textAlign="left" gutterBottom fontWeight="regular">
            Our Values
          </Typography>
          <Typography
            variant="body1"
            textAlign="left"
            color="text.secondary"
            sx={{ mb: 5 }}
          >
            The principles that guide everything we do
          </Typography>

          {/* 2x2 Grid: 2 per row on desktop/tablet, 1 per row on mobile */}
          <Grid 
            container 
            spacing={4}
            alignItems="stretch"
          >
            {values.map((value) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                key={value.title}
                sx={{ 
                  display: "flex",
                  justifyContent: "center"
                }}
              >
                <ValueCard sx={{ width: "100%", maxWidth: "100%" }}>
                  <CardContent sx={{ 
                    py: 4, 
                    px: 3,
                    width: "100%",
                    boxSizing: "border-box",
                    textAlign: "center"
                  }}>
                    <IconWrap>
                      {value.icon}
                    </IconWrap>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight="bold"
                      sx={{ 
                        mt: 2, 
                        mb: 1, 
                        color: "text.primary",
                        textAlign: "center",
                        width: "100%"
                      }}
                    >
                      {value.title}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ 
                        lineHeight: 1.6,
                        textAlign: "center",
                        width: "100%"
                      }}
                    >
                      {value.description}
                    </Typography>
                  </CardContent>
                </ValueCard>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Reviews (reads from Sanity) */}
        <ReviewSection />
      </Container>
    </>
  );
};

export default AboutPage;
